import type { DocumentNode } from '@apollo/client/core';
import type { Ref } from 'vue';
import { ref, unref } from 'vue';
import type { UseMutationOptions } from '@vue/apollo-composable';
import { useMutation } from '@vue/apollo-composable';
import type { Collection } from 'underscore';
import { reduce } from 'underscore';
import type {
  FieldNode,
  OperationDefinitionNode,
  SelectionNode,
} from 'graphql';
import { useI18n } from 'vue-i18n';
import type { ZvdZStandardModel } from '@/types';
import getDataFromMutationResult from '@/common/composition/get-data-from-mutation-result';
import { errorPopup } from '@/common/composition/useErrorPopup';

export default function useModelPostWithErrorResponse<
  Model = ZvdZStandardModel,
  Input = Model,
  // use "void" as Input to post with an empty input object
>(
  document: DocumentNode,
  options: UseMutationOptions | (() => UseMutationOptions) = {},
): {
  post: (input: Input, options?: UseMutationOptions) => Promise<boolean>;
  posting: Ref<boolean>;
  returnData: Ref<(ZvdZStandardModel & Model) | undefined>;
} {
  const { mutate, loading: posting } = useMutation<{ input: Model }>(
    document,
    options,
  );

  const { t } = useI18n({ useScope: 'global' });

  if (import.meta.env.MODE === 'development') {
    // this is a check to make sure the "popup" together with "showPopup" or "knownError" fields are in the query
    const definition = document.definitions[0] as OperationDefinitionNode;
    const firstSelectionNode = definition?.selectionSet
      .selections[0] as FieldNode;
    const selections = firstSelectionNode?.selectionSet?.selections;
    const findField = (
      nodes: Readonly<SelectionNode[]> | undefined,
      name: string,
    ) =>
      (nodes || []).find(
        node => node.kind === 'Field' && node.name.value === name,
      ) as FieldNode | undefined;
    const errorPopupField = findField(selections, 'popup');
    const showPopupField = findField(selections, 'showPopup');
    const knownErrorField = findField(selections, 'knownError');
    const validationField = findField(selections, 'validatie');
    const okField = findField(selections, 'ok');
    const successField = findField(selections, 'success');

    if (validationField) {
      const errorOkField = findField(
        validationField.selectionSet?.selections,
        'ok',
      );
      const errorMessageField = findField(
        validationField.selectionSet?.selections,
        'melding',
      );
      if (!errorMessageField || !errorOkField) {
        throw new Error(
          'To show errors coming from the server, you need to get the fields "validatie" containing the fields "ok" and "melding"',
        );
      }
    } else if (okField) {
      const meldingField = findField(selections, 'melding');
      if (!meldingField) {
        throw new Error(
          'To show errors coming from the server, you need to get the fields "ok" and "melding"',
        );
      }
    } else if (errorPopupField || showPopupField) {
      if (!errorPopupField) {
        throw new Error(
          'When you use "showPopup", you also need to add "popup"',
        );
      }
      if (!showPopupField) {
        throw new Error(
          'When you use "popup", you also need to add "showPopup"',
        );
      }
      const popupSelections = errorPopupField.selectionSet?.selections;
      const errorPopupTitle = findField(popupSelections, 'title');
      const errorPopupMessage = findField(popupSelections, 'message');
      if (!errorPopupTitle || !errorPopupMessage) {
        throw new Error(
          'The "popup" field needs to get the "title" and the "message" sub fields',
        );
      }
    } else if (successField) {
      const errorMessageField = findField(selections, 'errorMessage');
      if (!errorMessageField) {
        throw new Error(
          'The "errorMessage" is needed in combination with the "success" field',
        );
      }
    } else {
      if (!knownErrorField) {
        throw new Error(
          'To show errors coming from the server, you need to get the field "knownError"',
        );
      }

      const knownErrorSelections = knownErrorField.selectionSet?.selections;
      const keyField = findField(knownErrorSelections, 'key');
      if (!keyField) {
        throw new Error(
          'To show errors coming from the server, you need to get the fields "knownError" containing the field "key" and optionally "description" and "metaData"',
        );
      }
    }
  }

  const returnData = ref<ZvdZStandardModel & Model>();

  const post = async (
    input?: Input | Ref<Input>,
    overrideOptions?: UseMutationOptions,
  ) => {
    const unwrapped = reduce(
      (input ? unref(input) : {}) as Collection<unknown>,
      (result, value, key) => ({ ...result, [key]: unref(value) }),
      {},
    ) as Input;

    const result = await mutate({ input: unwrapped }, overrideOptions);

    const data = getDataFromMutationResult<ZvdZStandardModel & Model>(
      document,
      result,
    );
    if (!data) {
      return true;
    }
    returnData.value = data;
    if (
      data?.popup &&
      data.popup.message &&
      (data.showPopup === true || data.showPopup === undefined)
    ) {
      Object.assign(errorPopup, {
        title: data.popup.title,
        message: data.popup.message,
        open: true,
      });
      return false;
    }
    if (data.knownError?.key) {
      Object.assign(errorPopup, {
        knownErrorKey: data.knownError.key,
        metaData: data.knownError.metaData,
        message: data.knownError.description,
        open: true,
      });
      return false;
    }
    if ((data.validatie && !data.validatie.ok) || data.ok === false) {
      Object.assign(errorPopup, {
        title: t('validations.error'),
        message: data.validatie?.melding || data.melding,
        open: true,
      });
      return false;
    }
    if (data.success === false) {
      Object.assign(errorPopup, {
        title: t('validations.error'),
        message: data.errorMessage,
        open: true,
      });
      return false;
    }

    return true;
  };

  return { post, posting, returnData };
}
